'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { signIn } from 'next-auth/react';
import { Loader2, ArrowRight, MailCheck, ShieldCheck } from 'lucide-react';
import type { FlowChallenge, PromptField } from '@/lib/authentik-flow';
import {
  AUTHENTIK_BASE,
  isSupported,
  isAccessDenied,
  isFirstStage,
  flowForProduct,
  type FlowKind,
} from '@/lib/authentik-flow';
import { productByHost } from '@/lib/products';
import { celIZhdat } from '@/lib/metrika-celi';
import { ga4IZhdat } from '@/lib/ga4';
import { Link } from '@/i18n/routing';
import { GalkaRassylki } from '@/components/features/galka-rassylki';
import { brauzernoeHranilishche, zapomnitSRegistracii } from '@/lib/soglasie-rassylki';
import { estVybor, pohozhaNaPochtu } from '@/lib/vybor-pokupki';
import { zapomnitNamerenieAction } from '@/app/[locale]/(product-auth)/namerenie-actions';

/**
 * Универсальная форма для flow Authentik: вход, регистрация, восстановление.
 *
 * Стадии приходят из Authentik через наш серверный прокси, компонент их только
 * отображает. Логику (политики паролей, блокировки, MFA) исполняет Authentik.
 *
 * Незнакомую стадию не пытаемся угадать: уходим на страницу Authentik.
 * Лучше показать чужую рабочую форму, чем белый экран.
 */
export function FlowForm({
  kind,
  callbackUrl,
  token,
}: {
  kind: FlowKind;
  callbackUrl: string;
  /** Токен из письма: включает режим возврата по ссылке. */
  token?: string;
}) {
  const t = useTranslations('login');

  const [challenge, setChallenge] = useState<FlowChallenge | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  /** Сбой связи с Authentik, а не проблема со стадией. */
  const [unavailable, setUnavailable] = useState(false);
  /** Защита от повторной загрузки стадии при двойном вызове эффекта. */
  const loadedRef = useRef(false);
  /** Идёт отправка шага: защищает от двойного нажатия. */
  const sendingRef = useRef(false);
  /** Ссылка из письма уже использована. */
  const [spent, setSpent] = useState(false);
  /** Доступ к приложению закрыт: пароль верный, но входить некуда. */
  const [denied, setDenied] = useState(false);
  /**
   * Сломана настройка входа на нашей стороне.
   *
   * Отдельно от `denied`: там человеку нужно открыть доступ, а здесь он
   * бессилен и просить права бесполезно. Самый частый случай — адрес
   * возврата не зарегистрирован у клиента Authentik.
   */
  const [misconfigured, setMisconfigured] = useState(false);
  /**
   * Authentik сам оборвал попытку: чаще всего это пятый неверный пароль.
   * Хранит текст отказа от Authentik, чтобы показать его человеку.
   */
  const [rejected, setRejected] = useState<string | null>(null);
  /**
   * Согласие на рассылку: только на регистрации и снято по умолчанию.
   * При возврате по ссылке из письма (`token`) формы с полями нет, а
   * решение уже запомнено при первой отправке.
   */
  const [rassylka, setRassylka] = useState(false);
  const sGalkoj = kind === 'enrollment' && !token;

  const endpoint = token
    ? `/api/auth/flow/${kind}?token=${encodeURIComponent(token)}`
    : `/api/auth/flow/${kind}`;

  /**
   * Завершает вход после последнего шага flow.
   *
   * Обмен кода на токены делает сервер той же сессией, что вела flow.
   * Отправить браузер на страницу Authentik нельзя: сессия лежит в cookie
   * нашего домена, и пользователь снова увидел бы форму логина.
   */
  const finishLogin = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/flow/complete', { method: 'POST' });

      if (res.status === 403) {
        setDenied(true);
        return;
      }

      // Настройка входа сломана у нас. Предлагать тут «попросите доступ»
      // значит отправить человека по ложному следу.
      if (res.status === 500) {
        setMisconfigured(true);
        return;
      }

      const data = (await res.json()) as { ticket?: string };
      if (!res.ok || !data.ticket) {
        setFailed(true);
        return;
      }

      // Цель Метрики до перехода (#212): signIn уводит браузер, и
      // обычный вызов мог не успеть уйти. Восстановление пароля целью
      // не считается: это не новый человек и не вход по желанию.
      // GA4 (не на aisearch.ru) ждётся вместе с Метрикой, а не следом:
      // два предела подряд держали бы человека на форме вдвое дольше.
      if (kind === 'enrollment') {
        await Promise.all([celIZhdat('sign_up'), ga4IZhdat('sign_up', { method: 'email' })]);
      } else if (kind === 'authentication') await celIZhdat('login');

      await signIn('authentik-flow', { ticket: data.ticket, redirectTo: callbackUrl });
    } catch {
      setFailed(true);
    }
  }, [callbackUrl, kind]);

  const loadStage = useCallback(async () => {
    try {
      const res = await fetch(endpoint, { cache: 'no-store' });
      const data = (await res.json()) as FlowChallenge;
      if (data.component === 'xak-flow-unavailable') {
        setUnavailable(true);
        return;
      }
      if (!res.ok) {
        setFailed(true);
        return;
      }
      // Flow уже завершён: так бывает, когда предыдущий шаг был последним
      // или у Authentik осталась наша сессия. Без этой ветки пользователь
      // видел «нужен ещё один шаг», хотя вход фактически состоялся.
      if (data.component === 'xak-flow-redirect') {
        void finishLogin();
        return;
      }
      if (isAccessDenied(data.component)) {
        setRejected(data.error_message ?? '');
        return;
      }
      // Стадию, которую мы не рисуем, показывать нечем. После успешного
      // входа сюда попадает служебный ответ завершённого flow, и вместо
      // личного кабинета появлялось «нужен ещё один шаг».
      if (!isSupported(data.component)) {
        void finishLogin();
        return;
      }
      // Возврат по ссылке из письма, а flow начался с первой стадии.
      // Значит токен уже использован: подтверждение прошло раньше либо
      // ссылку открыли повторно. Показывать пустую форму регистрации
      // нельзя, человек решит, что его данные не сохранились.
      if (token && isFirstStage(data.component)) {
        setSpent(true);
        return;
      }
      setChallenge(data);
      setValues({});
    } catch {
      setFailed(true);
    }
  }, [endpoint, finishLogin, token]);

  useEffect(() => {
    // В режиме разработки React вызывает эффект дважды. Два параллельных
    // запроса к executor создают гонку: ответы приходят вразнобой и UI
    // показывает не ту стадию, которую видит сервер. Поэтому загружаем
    // стадию ровно один раз на монтирование.
    if (loadedRef.current) return;
    loadedRef.current = true;
    void loadStage();
  }, [loadStage]);

  /** Отправляет ответ на текущую стадию и переходит к следующей. */
  const send = useCallback(
    async (payload: Record<string, string>) => {
      // Проверяем по ref, а не по состоянию: setPending применяется
      // асинхронно, и два быстрых нажатия успевали проскочить проверку.
      // Второй запрос попадал в уже отработанную стадию и получал
      // ak-stage-flow-error вместо нормального ответа.
      if (sendingRef.current) return;
      sendingRef.current = true;
      setPending(true);

      // Выбор покупки на другое устройство (aisearch.ru): почту из формы
      // вместе с выбором запоминает API, и кабинет предложит продолжить,
      // даже если письмо откроют на телефоне. Не ждём ответа и не
      // смотрим на ошибки: вход идёт своим чередом. Домен проверяет
      // сервер; без выбора запрос не уходит вовсе.
      const pochta = Object.values(payload).find(pohozhaNaPochtu);
      if (pochta && !token && estVybor(callbackUrl, document.cookie)) {
        void zapomnitNamerenieAction(pochta, callbackUrl).catch(() => undefined);
      }
      try {
        const res = await fetch(`/api/auth/flow/${kind}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = (await res.json()) as FlowChallenge;

        // Связь с провайдером входа пропала: показываем это прямо,
        // а не как «нужен ещё один шаг».
        if (data.component === 'xak-flow-unavailable') {
          setUnavailable(true);
          return;
        }

        if (!res.ok) {
          setFailed(true);
          return;
        }

        if (data.component === 'xak-flow-advance') {
          await loadStage();
          return;
        }

        // Flow завершён. Дальше обычный OIDC-обмен: у Authentik уже есть
        // наша сессия, поэтому повторный ввод не потребуется.
        if (data.component === 'xak-flow-redirect') {
          void finishLogin();
          return;
        }

        // Стадии, которые мы не рисуем сами, показывать бессмысленно:
        // после успешного входа сюда попадает служебный ответ уже
        // завершённого flow, и пользователь видел «нужен ещё один шаг»
        // вместо личного кабинета. Проверяем, чем всё закончилось.
        // Отказ Authentik надо показать как есть. Считать его концом входа
        // нельзя: complete ответит 403, и человек с неверным паролем
        // прочитает «Пароль верный, но доступ не открыт».
        if (isAccessDenied(data.component)) {
          setRejected(data.error_message ?? '');
          return;
        }

        if (!isSupported(data.component)) {
          // Сессия шага уже отработана. Так бывает при повторной отправке
          // одной и той же формы: первый запрос перевёл flow дальше.
          // Нужно показать актуальную стадию, а не решать, что вход
          // завершён: иначе экран «письмо отправлено» подменялся
          // сообщением об отказе в доступе.
          if (data.component === 'ak-stage-flow-error') {
            await loadStage();
            return;
          }
          void finishLogin();
          return;
        }

        setChallenge(data);
        setValues({});
      } catch {
        setFailed(true);
      } finally {
        sendingRef.current = false;
        setPending(false);
      }
    },
    [kind, loadStage, finishLogin, callbackUrl, token],
  );

  const useRedirect = useCallback(() => {
    // Продукт определяется по домену в адресной строке: запасной путь обязан
    // вести на flow того же бренда, иначе человек с aisearch.tech попадёт на
    // форму регистрации ingdg.com.
    const host = window.location.hostname;
    const flow = flowForProduct(productByHost(host).id, kind, host);
    window.location.href = `${AUTHENTIK_BASE}/if/flow/${flow}/`;
  }, [kind]);

  /**
   * Начать заново: пользователь ввёл не тот идентификатор и хочет сменить его.
   * Сбрасываем flow на стороне Authentik, иначе он останется на стадии пароля
   * с чужим именем и выйти можно будет только перезагрузкой страницы.
   */
  const restart = useCallback(async () => {
    setPending(true);
    try {
      await fetch(`/api/auth/flow/${kind}`, { method: 'DELETE' });
    } catch {
      // Даже если сброс не удался, перезапрашиваем стадию: хуже не станет.
    } finally {
      setPending(false);
    }
    setChallenge(null);
    loadedRef.current = true;
    await loadStage();
  }, [kind, loadStage]);

  if (unavailable) {
    return (
      <div className="text-center py-2">
        <p className="text-[var(--color-text-muted)] mb-6 leading-relaxed">
          {t('unavailable_hint')}
        </p>
        <button
          onClick={() => {
            setUnavailable(false);
            void loadStage();
          }}
          className="w-full btn-primary mb-3"
        >
          {t('retry')}
        </button>
        <button
          type="button"
          onClick={() => {
            setUnavailable(false);
            void restart();
          }}
          className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
        >
          {t('start_over')}
        </button>
      </div>
    );
  }

  // Пароль верный, но приложение пользователю не открыто. Молчать здесь
  // нельзя: человек ввёл всё правильно и должен понимать, что дело не в нём.
  // Пароль верный, но сломана настройка входа у нас. Просить доступ
  // бесполезно: починить можем только мы.
  if (misconfigured) {
    return (
      <div className="text-center py-2">
        <p className="text-[var(--color-text-muted)] mb-6 leading-relaxed">
          {t('misconfigured_hint')}
        </p>
        <button
          type="button"
          onClick={() => {
            setMisconfigured(false);
            void restart();
          }}
          className="w-full btn-primary"
        >
          {t('start_over')}
        </button>
      </div>
    );
  }

  // Попытка оборвана Authentik. Пароль здесь как раз НЕ подошёл, поэтому
  // главное действие: восстановить пароль, а не писать в поддержку.
  if (rejected !== null) {
    return (
      <div className="text-center py-2">
        {rejected && <p className="text-sm text-red-400 mb-3">{rejected}</p>}
        <p className="text-[var(--color-text-muted)] mb-6 leading-relaxed">{t('rejected_hint')}</p>
        <Link href="/recover" className="btn-primary inline-block w-full mb-3">
          {t('forgot')}
        </Link>
        <button
          type="button"
          onClick={() => {
            setRejected(null);
            void restart();
          }}
          disabled={pending}
          className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
        >
          {t('start_over')}
        </button>
      </div>
    );
  }

  if (denied) {
    return (
      <div className="text-center py-2">
        <p className="text-[var(--color-text-muted)] mb-6 leading-relaxed">{t('denied_hint')}</p>
        <button
          type="button"
          onClick={() => {
            setDenied(false);
            void restart();
          }}
          className="w-full btn-primary"
        >
          {t('start_over')}
        </button>
      </div>
    );
  }

  // Ссылка из письма уже сработала. Обычно это значит, что почта
  // подтверждена: остаётся войти.
  if (spent) {
    return (
      <div className="text-center py-2">
        <ShieldCheck className="w-12 h-12 text-[var(--color-accent-400)] mx-auto mb-5" />
        <h2 className="text-lg font-semibold mb-2">{t('link_used_title')}</h2>
        <p className="text-[var(--color-text-muted)] text-sm mb-6 leading-relaxed">
          {t('link_used_desc')}
        </p>
        <Link href="/login" className="btn-primary inline-block w-full">
          {t('to_login')}
        </Link>
      </div>
    );
  }

  if (failed) {
    return (
      <div className="text-center">
        <p className="text-[var(--color-text-muted)] mb-6">{t('fallback_hint')}</p>
        <button onClick={useRedirect} className="w-full btn-primary">
          {t('button')}
        </button>
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--color-text-muted)]" />
      </div>
    );
  }

  const errors = challenge.response_errors ?? {};

  /**
   * Ошибки конкретного поля.
   *
   * Общие ошибки сюда не подмешиваются. Authentik присылает часть проверок
   * как non_field_errors: например, «Password needs to be 8 characters or
   * longer». Раньше они добавлялись к каждому полю, и при регистрации все
   * четыре поля загорались красным с текстом про пароль. Общие ошибки
   * показываются один раз, отдельным блоком.
   */
  const errorsFor = (key: string) => errors[key] ?? [];

  /** Ошибки, не привязанные к полю. */
  const commonErrors = errors.non_field_errors ?? [];

  // Письмо отправлено: ждём, пока человек перейдёт по ссылке.
  if (challenge.component === 'ak-stage-email') {
    return (
      <div className="text-center py-4">
        <MailCheck className="w-12 h-12 text-[var(--color-accent-400)] mx-auto mb-5" />
        <h2 className="text-lg font-semibold mb-2">{t('email_sent_title')}</h2>
        <p className="text-[var(--color-text-muted)] text-sm leading-relaxed">
          {t('email_sent_desc')}
        </p>
      </div>
    );
  }

  // Согласие при возврате по ссылке из письма. Стадию вставляет сам Authentik,
  // отключить её нельзя: это защита от почтовых сканеров, открывающих ссылки.
  if (challenge.component === 'ak-stage-consent') {
    return (
      <div className="text-center py-2">
        <ShieldCheck className="w-12 h-12 text-[var(--color-accent-400)] mx-auto mb-5" />
        <h2 className="text-lg font-semibold mb-2">{t('consent_title')}</h2>
        <p className="text-[var(--color-text-muted)] text-sm mb-6 leading-relaxed">
          {challenge.pending_user ? challenge.pending_user : t('consent_desc')}
        </p>
        <button
          onClick={() => {
            // Отправляем токен из тела стадии, а не тот, что был в письме.
            void send({ component: 'ak-stage-consent', token: challenge.token ?? '' });
          }}
          disabled={pending}
          className="w-full btn-primary"
        >
          {pending ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t('consent_confirm')}
        </button>
      </div>
    );
  }

  // Набор полей задаёт Authentik: регистрация, новый пароль и т.п.
  if (challenge.component === 'ak-stage-prompt') {
    const fields: PromptField[] = challenge.fields ?? [];
    // Подпись кнопки зависит от сценария: «Создать аккаунт» на регистрации,
    // «Сохранить» при смене пароля, «Войти» в остальных случаях.
    const submitLabel =
      kind === 'enrollment' ? t('create_account') : kind === 'recovery' ? t('save') : t('submit');
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          // Решение запоминается до отправки, а не после: при успехе
          // браузер уходит в подтверждение почты или в кабинет, и
          // «после» может не наступить.
          if (sGalkoj) zapomnitSRegistracii(brauzernoeHranilishche(), rassylka);
          void send({ component: 'ak-stage-prompt', ...values });
        }}
        className="space-y-4"
      >
        {fields.map((field) => (
          <div key={field.field_key}>
            <label
              htmlFor={field.field_key}
              className="block text-sm font-medium text-[var(--color-text)] mb-2"
            >
              {/* Authentik присылает подписи по-английски. Известные поля
                  переводим, для остальных показываем как есть. */}
              {t.has(`field_${field.field_key}`) ? t(`field_${field.field_key}`) : field.label}
            </label>
            <input
              id={field.field_key}
              name={field.field_key}
              type={
                field.type === 'password' ? 'password' : field.type === 'email' ? 'email' : 'text'
              }
              value={values[field.field_key] ?? field.initial_value ?? ''}
              onChange={(e) => {
                setValues((prev) => ({ ...prev, [field.field_key]: e.target.value }));
              }}
              required={field.required}
              disabled={pending}
              autoComplete={field.type === 'password' ? 'new-password' : field.field_key}
              className="input-base"
            />
            {field.sub_text && (
              <p className="mt-1.5 text-xs text-[var(--color-text-muted)]">{field.sub_text}</p>
            )}
            <FieldErrors items={errorsFor(field.field_key)} />
          </div>
        ))}

        <FieldErrors items={commonErrors} />

        {sGalkoj && <GalkaRassylki checked={rassylka} onChange={setRassylka} disabled={pending} />}

        <button type="submit" disabled={pending} className="w-full btn-primary">
          {pending ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : submitLabel}
        </button>
      </form>
    );
  }

  // Вход: идентификатор и пароль.
  const isPassword = challenge.component === 'ak-stage-password';
  const isIdentification = challenge.component === 'ak-stage-identification';

  if (!isPassword && !isIdentification) {
    return (
      <div className="text-center">
        <p className="text-[var(--color-text-muted)] mb-6">{t('extra_step')}</p>
        {/* Сначала предлагаем начать заново на нашем сайте. Уход на страницу
            Authentik при мёртвой сессии возвращал пользователя на этот же
            экран, и получался круг без выхода. */}
        <button
          type="button"
          onClick={() => void restart()}
          disabled={pending}
          className="w-full btn-primary mb-3 disabled:opacity-50"
        >
          {t('start_over')}
        </button>
        <button
          onClick={useRedirect}
          className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-accent-400)] transition-colors"
        >
          {t('continue')}
        </button>
      </div>
    );
  }

  const key = isPassword ? 'password' : 'uid_field';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void send({ [key]: values[key] ?? '' });
      }}
      className="space-y-4"
    >
      {isPassword && challenge.pending_user && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <span className="text-[var(--color-text-muted)]">{challenge.pending_user}</span>
          <button
            type="button"
            onClick={() => void restart()}
            disabled={pending}
            className="text-[var(--color-accent-400)] hover:underline disabled:opacity-50"
          >
            {t('not_you')}
          </button>
        </div>
      )}

      <div>
        <label htmlFor={key} className="block text-sm font-medium text-[var(--color-text)] mb-2">
          {isPassword ? t('password') : t('identifier')}
        </label>
        <input
          id={key}
          name={isPassword ? 'password' : 'username'}
          type={isPassword ? 'password' : 'text'}
          value={values[key] ?? ''}
          onChange={(e) => {
            setValues({ [key]: e.target.value });
          }}
          autoComplete={isPassword ? 'current-password' : 'username'}
          autoFocus
          required
          disabled={pending}
          className="input-base"
        />
        <FieldErrors items={errorsFor(key)} />
      </div>

      <FieldErrors items={commonErrors} />

      <button
        type="submit"
        disabled={pending || !(values[key] ?? '').trim()}
        className="w-full btn-primary"
      >
        {pending ? (
          <Loader2 className="w-4 h-4 animate-spin mx-auto" />
        ) : (
          <span className="inline-flex items-center gap-2">
            {isPassword ? t('submit') : t('next')}
            <ArrowRight className="w-4 h-4" />
          </span>
        )}
      </button>
    </form>
  );
}

function FieldErrors({ items }: { items: { string: string; code: string }[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="mt-2 space-y-1">
      {items.map((err, i) => (
        <li key={i} className="text-sm text-red-400">
          {err.string}
        </li>
      ))}
    </ul>
  );
}
